import {
  doc,
  getDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  PRO_MONTHLY_PRICING_TIERS,
  PRO_ANNUAL_PRICING_TIERS,
  BUSINESS_MONTHLY_PRICE,
  BUSINESS_ANNUAL_PRICE,
  type ProAnnualPricingTier,
  getProAnnualPrice,
} from '../config/proPlanPricing';

export interface PricingTier {
  subscribers: number;
  price: number;
}

export interface PlanFeature {
  id: string;
  name: string;
  included: boolean;
  value?: string;
}

export interface PlanMetadata {
  description: string;
  features: PlanFeature[];
  trialDays: number;
  isPopular?: boolean;
  ctaLabel?: string;
  limits: {
    contacts: number | 'unlimited';
    emailsPerMonth: number | 'unlimited';
    workspaces: number;
  };
  billingCycles: {
    monthly: boolean;
    yearly: boolean;
    yearlyDiscount: number; // percentage
  };
}

export interface PlanPricing {
  starter: {
    metadata: PlanMetadata;
  };
  pro: {
    tiers: PricingTier[];
    annualTiers?: ProAnnualPricingTier[];
    metadata: PlanMetadata;
  };
  business: {
    tiers: PricingTier[];
    metadata: PlanMetadata;
  };
  lastUpdated: Timestamp;
  updatedBy: string;
}

const DEFAULT_STARTER_FEATURES: PlanFeature[] = [
  { id: 'automation', name: 'Basic Automation', included: true },
  { id: 'analytics', name: 'Basic Analytics', included: true },
  { id: 'templates', name: 'Standard Templates', included: true },
  { id: 'support', name: 'Email Support', included: true },
  { id: 'dns', name: 'Custom DNS', included: false },
  { id: 'whitelabel', name: 'Scubamail Branding', included: true },
];

const DEFAULT_STARTER_METADATA: PlanMetadata = {
  description: 'Essential tools to build your audience and send professional newsletters with ease.',
  features: DEFAULT_STARTER_FEATURES,
  trialDays: 0,
  isPopular: false,
  ctaLabel: 'Start for free',
  limits: {
    contacts: 1000,
    emailsPerMonth: 12000,
    workspaces: 1
  },
  billingCycles: {
    monthly: true,
    yearly: false,
    yearlyDiscount: 0
  }
};

const DEFAULT_PRO_FEATURES: PlanFeature[] = [
  { id: 'automation', name: 'Advanced Automation', included: true },
  { id: 'analytics', name: 'Pro Analytics', included: true },
  { id: 'templates', name: 'Premium Templates', included: true },
  { id: 'support', name: 'Priority Support', included: true },
  { id: 'dns', name: 'Custom DNS', included: true },
  { id: 'whitelabel', name: 'No Branding', included: true },
];

const DEFAULT_BUSINESS_FEATURES: PlanFeature[] = [
  { id: 'automation', name: 'Unlimited Automation', included: true },
  { id: 'analytics', name: 'Custom Analytics', included: true },
  { id: 'templates', name: 'Enterprise Templates', included: true },
  { id: 'support', name: '24/7 Dedicated Support', included: true },
  { id: 'dns', name: 'Advanced DNS Management', included: true },
  { id: 'whitelabel', name: 'Full White Labeling', included: true },
  { id: 'api', name: 'API Access', included: true },
];

const DEFAULT_PRO_METADATA: PlanMetadata = {
  description: 'Maximize engagement with advanced automation, deep analytics, and white-labeled emails.',
  features: DEFAULT_PRO_FEATURES,
  trialDays: 14,
  isPopular: true,
  ctaLabel: 'Get Pro Now',
  limits: {
    contacts: 50000,
    emailsPerMonth: 500000,
    workspaces: 5
  },
  billingCycles: {
    monthly: true,
    yearly: true,
    yearlyDiscount: 20
  }
};

const DEFAULT_BUSINESS_METADATA: PlanMetadata = {
  description: 'The ultimate scaling engine for high-volume teams requiring 24/7 priority support and enterprise-grade deliverability.',
  features: DEFAULT_BUSINESS_FEATURES,
  trialDays: 14,
  isPopular: false,
  ctaLabel: 'Start Business',
  limits: {
    contacts: 'unlimited',
    emailsPerMonth: 'unlimited',
    workspaces: 20
  },
  billingCycles: {
    monthly: true,
    yearly: true,
    yearlyDiscount: 17  // $2,490/yr vs $2,988/yr = ~17% off
  }
};

const DEFAULT_PRO_PRICING: PricingTier[] = PRO_MONTHLY_PRICING_TIERS.map((t) => ({
  subscribers: t.subscribers,
  price: t.price,
}));

const DEFAULT_PRO_ANNUAL_PRICING: ProAnnualPricingTier[] = [...PRO_ANNUAL_PRICING_TIERS];

/** Business is flat-rate — single sentinel entry so tier-iterating code still works. */
const DEFAULT_BUSINESS_PRICING: PricingTier[] = [
  { subscribers: 0, price: BUSINESS_MONTHLY_PRICE },
];

const PRICING_DOC_PATH = 'system/planPricing';

class PlanPricingService {
  /**
   * Get current plan pricing from Firestore
   * Returns default pricing if not set in Firestore
   */
  async getPlanPricing(): Promise<PlanPricing> {
    try {
      const pricingDoc = await getDoc(doc(db, PRICING_DOC_PATH));

      if (pricingDoc.exists()) {
        const data = pricingDoc.data();
        return {
          starter: {
            metadata: data.starter?.metadata || DEFAULT_STARTER_METADATA
          },
          pro: {
            tiers: data.pro?.tiers || DEFAULT_PRO_PRICING,
            annualTiers: data.pro?.annualTiers || DEFAULT_PRO_ANNUAL_PRICING,
            metadata: data.pro?.metadata || DEFAULT_PRO_METADATA
          },
          business: {
            tiers: data.business?.tiers || DEFAULT_BUSINESS_PRICING,
            metadata: data.business?.metadata || DEFAULT_BUSINESS_METADATA
          },
          lastUpdated: data.lastUpdated || Timestamp.now(),
          updatedBy: data.updatedBy || 'system'
        };
      }

      // Return defaults if not set
      return {
        starter: { metadata: DEFAULT_STARTER_METADATA },
        pro: {
          tiers: DEFAULT_PRO_PRICING,
          annualTiers: DEFAULT_PRO_ANNUAL_PRICING,
          metadata: DEFAULT_PRO_METADATA,
        },
        business: { tiers: DEFAULT_BUSINESS_PRICING, metadata: DEFAULT_BUSINESS_METADATA },
        lastUpdated: Timestamp.now(),
        updatedBy: 'system'
      };
    } catch (error) {
      console.error('Error fetching plan pricing:', error);
      // Return defaults on error
      return {
        starter: { metadata: DEFAULT_STARTER_METADATA },
        pro: {
          tiers: DEFAULT_PRO_PRICING,
          annualTiers: DEFAULT_PRO_ANNUAL_PRICING,
          metadata: DEFAULT_PRO_METADATA,
        },
        business: { tiers: DEFAULT_BUSINESS_PRICING, metadata: DEFAULT_BUSINESS_METADATA },
        lastUpdated: Timestamp.now(),
        updatedBy: 'system'
      };
    }
  }

  /**
   * Update plan pricing in Firestore
   */
  async updatePlanPricing(
    pricing: {
      starter?: { metadata?: PlanMetadata };
      pro?: { tiers?: PricingTier[]; annualTiers?: ProAnnualPricingTier[]; metadata?: PlanMetadata };
      business?: { tiers?: PricingTier[]; metadata?: PlanMetadata };
    },
    updatedBy: string
  ): Promise<void> {
    try {
      const currentPricing = await this.getPlanPricing();

      const updatedPricing: PlanPricing = {
        starter: {
          metadata: pricing.starter?.metadata || currentPricing.starter.metadata
        },
        pro: {
          tiers: pricing.pro?.tiers || currentPricing.pro.tiers,
          annualTiers: pricing.pro?.annualTiers || currentPricing.pro.annualTiers || DEFAULT_PRO_ANNUAL_PRICING,
          metadata: pricing.pro?.metadata || currentPricing.pro.metadata
        },
        business: {
          tiers: pricing.business?.tiers || currentPricing.business.tiers,
          metadata: pricing.business?.metadata || currentPricing.business.metadata
        },
        lastUpdated: Timestamp.now(),
        updatedBy: updatedBy
      };

      await setDoc(doc(db, PRICING_DOC_PATH), updatedPricing);
    } catch (error) {
      console.error('Error updating plan pricing:', error);
      throw new Error('Failed to update plan pricing');
    }
  }

  /**
   * Get pricing tier for a specific plan and subscriber count
   */
  async getPriceForSubscribers(
    plan: 'pro' | 'business',
    subscribers: number
  ): Promise<number> {
    // Business is a flat rate — always $249/mo regardless of subscriber count
    if (plan === 'business') return BUSINESS_MONTHLY_PRICE;

    const pricing = await this.getPlanPricing();
    const tiers = pricing.pro.tiers;

    // If subscribers is less than the minimum tier, use the first tier
    if (subscribers < tiers[0].subscribers) {
      return tiers[0].price;
    }

    // Find the appropriate tier for the subscriber count
    const tier = tiers.find(tier => subscribers <= tier.subscribers) || tiers[tiers.length - 1];
    return tier.price;
  }

  /**
   * Get all pricing tiers for a plan
   */
  async getPricingTiers(plan: 'pro' | 'business'): Promise<PricingTier[]> {
    const pricing = await this.getPlanPricing();
    return plan === 'pro' ? pricing.pro.tiers : pricing.business.tiers;
  }

  async getProAnnualPriceForSubscribers(subscribers: number): Promise<number> {
    const pricing = await this.getPlanPricing();
    const tiers = pricing.pro.annualTiers || DEFAULT_PRO_ANNUAL_PRICING;
    return getProAnnualPrice(subscribers, tiers);
  }

  async getProAnnualTiers(): Promise<ProAnnualPricingTier[]> {
    const pricing = await this.getPlanPricing();
    return pricing.pro.annualTiers || DEFAULT_PRO_ANNUAL_PRICING;
  }

  /**
   * Reset pricing to defaults
   */
  async resetToDefaults(updatedBy: string): Promise<void> {
    await this.updatePlanPricing(
      {
        pro: {
          tiers: DEFAULT_PRO_PRICING,
          annualTiers: DEFAULT_PRO_ANNUAL_PRICING,
          metadata: DEFAULT_PRO_METADATA,
        },
        business: { tiers: DEFAULT_BUSINESS_PRICING, metadata: DEFAULT_BUSINESS_METADATA }
      },
      updatedBy
    );
  }
}

export const planPricingService = new PlanPricingService();

